import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Button from "../../../components/base/Button";
import { convex } from "../../../convex/client";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    recaptchaResponse: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const recaptchaWidgetId = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;
    let hasRendered = false;

    const initializeRecaptcha = () => {
      if (!isMounted || hasRendered) return;

      if (window.grecaptcha?.render && recaptchaRef.current) {
        const existingWidget =
          recaptchaRef.current.querySelector(".g-recaptcha");
        if (existingWidget) {
          console.log("reCAPTCHA already rendered, skipping...");
          setRecaptchaLoaded(true);
          hasRendered = true;
          return;
        }

        if (recaptchaRef.current) {
          recaptchaRef.current.innerHTML = "";
        }

        try {
          const widgetId = window.grecaptcha.render(recaptchaRef.current, {
            sitekey:
              import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
              "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI",
            callback: (response: string) => {
              if (isMounted) {
                setFormData((prev) => ({
                  ...prev,
                  recaptchaResponse: response,
                }));
              }
            },
            "expired-callback": () => {
              if (isMounted) {
                setFormData((prev) => ({ ...prev, recaptchaResponse: "" }));
              }
            },
            "error-callback": () => {
              if (isMounted) {
                setFormData((prev) => ({ ...prev, recaptchaResponse: "" }));
              }
            },
          });

          recaptchaWidgetId.current = widgetId;
          setRecaptchaLoaded(true);
          hasRendered = true;
        } catch (error) {
          console.error("Error rendering reCAPTCHA:", error);
          // Don't retry if it's already rendered error
          if (
            typeof error === "object" &&
            error !== null &&
            "message" in error &&
            typeof (error as { message: unknown }).message === "string" &&
            (error as { message: string }).message.includes(
              "already been rendered"
            )
          ) {
            hasRendered = true;
            setRecaptchaLoaded(true);
          } else {
            setTimeout(() => {
              if (isMounted && !hasRendered) {
                initializeRecaptcha();
              }
            }, 1000);
          }
        }
      } else {
        setTimeout(initializeRecaptcha, 100);
      }
    };

    if (window.grecaptcha && recaptchaRef.current) {
      initializeRecaptcha();
    } else {
      const checkInterval = setInterval(() => {
        if (
          window.grecaptcha &&
          recaptchaRef.current &&
          isMounted &&
          !hasRendered
        ) {
          clearInterval(checkInterval);
          initializeRecaptcha();
        }
      }, 100);

      return () => {
        clearInterval(checkInterval);
        isMounted = false;
      };
    }

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (recaptchaWidgetId.current !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      }
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!privacyAccepted) {
      alert(
        "By submitting this form you agree to the terms of the Privacy Policy. Please check the box to continue."
      );
      return;
    }

    // Check reCAPTCHA v2
    if (!recaptchaLoaded) {
      alert("reCAPTCHA is still loading. Please wait a moment and try again.");
      return;
    }

    if (!formData.recaptchaResponse) {
      alert("Please complete the reCAPTCHA verification.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Store in Convex database
      await convex.mutation("contactForm:submitContactForm" as any, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        recaptchaResponse: formData.recaptchaResponse,
      });

      // Send email via Convex Action (works on Vercel)
      await convex.action("sendEmail:sendContactEmail" as any, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        recaptchaResponse: formData.recaptchaResponse,
      });

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
        recaptchaResponse: "",
      });
      setPrivacyAccepted(false);

      // Reset reCAPTCHA
      if (recaptchaWidgetId.current !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: "ri-phone-line",
      title: "Phone",
      details: "(617) 765-5275",
      description: "Call us for appointments or urgent inquiries",
    },
    {
      icon: "ri-mail-line",
      title: "Email",
      details: "pamaghealthcareservices@gmail.com",
      description: "Send us a message anytime",
    },
    {
      icon: "ri-map-pin-line",
      title: "Location",
      details: "925 Washington Street, Suite 12, Dorchester 02124 MA",
      description: "Visit our modern healthcare facility",
    },
    {
      icon: "ri-time-line",
      title: "Hours",
      details: "Mon-Fri: 8AM-6PM, Sat: 9AM-2PM",
      description: "We're here when you need us",
    },
  ];

  const PrivacyModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Privacy Policy</h3>
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
            <p>
              <strong>Last Updated:</strong>{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
              })}
            </p>

            <p>
              At <strong>Pamag Healthcare Services LLC</strong>, we value your
              privacy and are committed to protecting your personal information.
              This Privacy Policy explains how we collect, use, and safeguard
              your information when you visit our website or use our healthcare
              services.
            </p>

            <p>
              By using our website or services, you consent to the practices
              described in this Privacy Policy. We encourage you to read this
              policy to understand how we handle your information.
            </p>

            <h4 className="text-lg font-semibold text-gray-900">
              Information We Collect
            </h4>
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Personal Information:</strong> Your name, email address,
                phone number, and any information you provide when contacting us
                or filling out forms
              </li>
              <li>
                <strong>Health Information:</strong> Medical history, health
                conditions, and care preferences you share with us
              </li>
              <li>
                <strong>Usage Information:</strong> How you interact with our
                website, including pages visited and time spent browsing
              </li>
              <li>
                <strong>Communication Records:</strong> Messages, emails, and
                phone calls between you and our team
              </li>
              <li>
                <strong>Cookies and Tracking:</strong> We use cookies to improve
                your browsing experience and understand how you use our website
              </li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900">
              How We Use Your Information
            </h4>
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide and improve our healthcare services</li>
              <li>Communicate with you about appointments and care</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you updates about our services (with your consent)</li>
              <li>Improve our website and user experience</li>
              <li>Comply with legal and regulatory requirements</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900">
              Sharing Your Information
            </h4>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Healthcare Providers:</strong> Other medical
                professionals involved in your care (with your permission)
              </li>
              <li>
                <strong>Family Members:</strong> Designated contacts in
                emergency situations or with your authorization
              </li>
              <li>
                <strong>Service Providers:</strong> Trusted partners who help us
                deliver our services
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to
                protect your safety
              </li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900">
              Your Choices
            </h4>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Access and update your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Request corrections to inaccurate information</li>
              <li>Ask questions about how we use your data</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900">
              Data Security
            </h4>
            <p>
              We implement comprehensive security measures to protect your
              personal information, including encryption, secure data storage,
              and regular security updates. Your information is handled with the
              highest level of care and protection.
            </p>

            <h4 className="text-lg font-semibold text-gray-900">
              Changes to This Policy
            </h4>
            <p>
              We may update this Privacy Policy from time to time. When we do,
              we'll post the updated policy on our website and update the "Last
              Updated" date. We encourage you to review this policy
              periodically.
            </p>

            <h4 className="text-lg font-semibold text-gray-900">Contact Us</h4>
            <p>
              If you have any questions about this Privacy Policy or our privacy
              practices, please don't hesitate to contact us:
            </p>
            <ul className="list-none pl-0 space-y-1">
              <li>
                <strong>Email:</strong> pamaghealthcareservices@gmail.com
              </li>
              <li>
                <strong>Phone:</strong> (617) 765-5275
              </li>
              <li>
                <strong>Address:</strong> Washington Street, Suite 12,
                Dorchester, MA 02124
              </li>
            </ul>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Get In Touch
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Ready to experience exceptional healthcare? Contact us today to
            schedule your appointment or learn more about our services.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6 sm:mb-8">
              Contact Information
            </h3>
            <div className="space-y-4 sm:space-y-6">
              {contactInfo.map((info) => (
                <div key={info.title} className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                    <i
                      className={`${info.icon} text-lg sm:text-xl text-teal-600`}
                    ></i>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                      {info.title}
                    </h4>
                    {info.title === "Phone" ? (
                      <a
                        href={`tel:${info.details.replace(/[^0-9+]/g, "")}`}
                        className="text-gray-800 font-medium mb-1 text-sm sm:text-base hover:text-teal-600 transition-colors cursor-pointer inline-block"
                      >
                        {info.details}
                      </a>
                    ) : info.title === "Email" ? (
                      <a
                        href={`mailto:${info.details}`}
                        className="text-gray-800 font-medium mb-1 text-sm sm:text-base hover:text-teal-600 transition-colors cursor-pointer inline-block break-all"
                      >
                        {info.details}
                      </a>
                    ) : (
                      <p className="text-gray-800 font-medium mb-1 text-sm sm:text-base">
                        {info.details}
                      </p>
                    )}
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {info.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 sm:mt-12">
              <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                Find Us
              </h4>
              <div className="bg-white rounded-lg overflow-hidden shadow-md">
                <iframe
                  src="https://maps.google.com/maps?q=925+Washington+Street+Suite+12+Dorchester+Boston+MA+02124&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  className="sm:h-250"
                  style={{ border: 0 }}
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pamag Healthcare Services Location Map"
                ></iframe>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                Send Us a Message
              </h3>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="service"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                    >
                      Service of Interest
                    </label>
                    <div className="relative">
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select a service</option>
                        <option value="personal-care">
                          Personal Care Assistance
                        </option>
                        <option value="skilled-nursing">
                          Skilled Nursing & Specialized Care
                        </option>
                        <option value="companion-services">
                          Companion Services
                        </option>
                        <option value="light-housekeeping">
                          Light Housekeeping
                        </option>
                        <option value="meal-preparation">
                          Meal Preparation & Nutrition
                        </option>
                        <option value="care-management">Care Management</option>
                        <option value="transportation">
                          Transportation Services
                        </option>
                        <option value="physical-therapy">
                          Physical Therapy
                        </option>
                        <option value="speech-therapy">Speech Therapy</option>
                        <option value="occupational-therapy">
                          Occupational Therapy
                        </option>
                        <option value="cna-hha">CNA/HHA Services</option>
                        <option value="around-the-clock">
                          Around-the-Clock Care Services
                        </option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label
                    htmlFor="message"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-vertical"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Privacy Policy Agreement */}
                <div className="mb-4 sm:mb-6">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-0.5 flex-shrink-0"
                      required
                    />
                    <span className="ml-2 text-xs sm:text-sm text-gray-700">
                      <span className="text-red-500">*</span> By submitting this
                      form you agree to the terms of the{" "}
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-teal-600 hover:text-teal-700 underline cursor-pointer"
                      >
                        Privacy Policy
                      </button>
                    </span>
                  </label>
                </div>

                {/* reCAPTCHA v2 Widget */}
                <div className="mb-4 sm:mb-6">
                  <div ref={recaptchaRef} className="flex justify-center"></div>
                </div>

                {submitStatus === "success" && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2 text-sm sm:text-base"></i>
                      <p className="text-green-800 text-xs sm:text-sm">
                        Thank you! Your message has been sent successfully.
                        We'll get back to you soon.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2 text-sm sm:text-base"></i>
                      <p className="text-red-800 text-xs sm:text-sm">
                        Sorry, there was an error sending your message. Please
                        try again.
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full text-sm sm:text-base py-2 sm:py-3"
                  onClick={() => {}}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>{" "}
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i> Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {showPrivacyModal && <PrivacyModal />}
    </section>
  );
}
